import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SUPPLIERS, MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";
import { formatApiErrorDetail } from "@/lib/api";

const MAX_FILE = 5 * 1024 * 1024;

export default function Fournisseurs() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "", categories: [], territories: "",
    delivery_capabilities: "", rental_services: "", brands: "", website: "", comments: "",
    consent: false, company_website: "",
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const toggleCategory = (slug) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug],
    }));
  };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setFileError("");
    if (!f) return;
    if (f.size > MAX_FILE) {
      setFileError(lang === "fr" ? "Le fichier dépasse 5 Mo." : "The file exceeds 5 MB.");
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const errs = {};
    if (!form.company.trim()) errs.company = t(SUPPLIERS.required);
    if (!form.contact_name.trim()) errs.contact_name = t(SUPPLIERS.required);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t(SUPPLIERS.invalidEmail);
    if (form.categories.length === 0) errs.categories = t(SUPPLIERS.required);
    if (!form.territories.trim()) errs.territories = t(SUPPLIERS.required);
    if (!form.consent) errs.consent = t(SUPPLIERS.required);
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    setServerError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "consent") fd.append(k, v ? "true" : "false");
        else if (k === "categories") fd.append(k, v.join(","));
        else if (v !== "" && v != null) fd.append(k, v);
      });
      fd.append("lang", lang);
      if (file) fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(formatApiErrorDetail(data.detail || data.error));
      setStatus("success");
    } catch (err) {
      setServerError(formatApiErrorDetail(err.message));
      setStatus("error");
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy-950 outline-none transition-colors placeholder:text-steel/70 focus:border-electric ${errors[k] ? "border-red-400" : "border-slate-200"}`;
  const err = (k) => errors[k] && <p role="alert" className="mt-1 text-xs text-red-500">{errors[k]}</p>;

  if (status === "success") {
    return (
      <main className="bg-paper pb-24 pt-[152px] md:pt-[200px]" data-testid="supplier-success-page">
        <div className="mx-auto max-w-2xl px-5 md:px-8">
          <div className="rounded-2xl border border-slate-200/90 bg-white p-10 text-center shadow-card">
            <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-badge text-electric"><CheckCircle2 size={32} /></span>
            <h1 className="mt-6 font-display text-2xl font-bold text-navy-950 md:text-3xl" data-testid="supplier-success-title">{t(SUPPLIERS.success)}</h1>
            <button onClick={() => { setStatus("idle"); setForm({ ...form, categories: [], consent: false }); }} className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-navy-800 transition-colors hover:border-electric hover:text-electric" data-testid="supplier-success-reset">
              {t(SUPPLIERS.again)}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="suppliers-page">
      <Seo
        title={lang === "fr"
          ? "Devenir fournisseur partenaire — MOON Supply | Export Monde Prestige Inc."
          : "Become a partner supplier — MOON Supply | Export Monde Prestige Inc."}
        description={t(SUPPLIERS.header.sub)}
        path="/fournisseurs"
      />
      <section className="bg-gradient-to-b from-ice via-white to-white pb-12 pt-[152px] md:pt-[200px]">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(SUPPLIERS.header.eyebrow)}</p>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.06] tracking-tight text-navy-950 md:text-6xl">{t(SUPPLIERS.header.title)}</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-graphite md:text-lg">{t(SUPPLIERS.header.sub)}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Reveal>
            <form onSubmit={onSubmit} noValidate className="rounded-2xl border border-slate-200/90 bg-white p-7 shadow-card md:p-10" data-testid="supplier-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="s-company" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.company)} *</label>
                  <input id="s-company" data-testid="supplier-company-input" className={inputCls("company")} value={form.company} onChange={set("company")} />
                  {err("company")}
                </div>
                <div>
                  <label htmlFor="s-contact" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.contactName)} *</label>
                  <input id="s-contact" data-testid="supplier-contact-input" className={inputCls("contact_name")} value={form.contact_name} onChange={set("contact_name")} />
                  {err("contact_name")}
                </div>
                <div>
                  <label htmlFor="s-email" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.email)} *</label>
                  <input id="s-email" type="email" data-testid="supplier-email-input" className={inputCls("email")} value={form.email} onChange={set("email")} />
                  {err("email")}
                </div>
                <div>
                  <label htmlFor="s-phone" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.phone)}</label>
                  <input id="s-phone" type="tel" data-testid="supplier-phone-input" className={inputCls("phone")} value={form.phone} onChange={set("phone")} />
                </div>
              </div>

              <div className="mt-5" data-testid="supplier-categories-group">
                <p className="mb-2 text-[13px] font-bold text-navy-900">{t(SUPPLIERS.categoriesLabel)} *</p>
                <div className="flex flex-wrap gap-2">
                  {MOON.categories.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => toggleCategory(c.slug)}
                      aria-pressed={form.categories.includes(c.slug)}
                      data-testid={`supplier-category-${c.slug}`}
                      className={`rounded-full border px-4 py-2 text-[12.5px] font-semibold transition-colors ${form.categories.includes(c.slug) ? "border-electric bg-electric text-white" : "border-slate-200 bg-white text-graphite hover:border-electric hover:text-electric"}`}
                    >
                      {t(c.label)}
                    </button>
                  ))}
                </div>
                {err("categories")}
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="s-territories" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.territories)} *</label>
                  <input id="s-territories" data-testid="supplier-territories-input" placeholder={t(SUPPLIERS.territoriesPlaceholder)} className={inputCls("territories")} value={form.territories} onChange={set("territories")} />
                  {err("territories")}
                </div>
                <div>
                  <label htmlFor="s-delivery" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.delivery)}</label>
                  <input id="s-delivery" data-testid="supplier-delivery-input" placeholder={t(SUPPLIERS.deliveryPlaceholder)} className={inputCls("delivery_capabilities")} value={form.delivery_capabilities} onChange={set("delivery_capabilities")} />
                </div>
                <div>
                  <label htmlFor="s-rental" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.rental)}</label>
                  <input id="s-rental" data-testid="supplier-rental-input" className={inputCls("rental_services")} value={form.rental_services} onChange={set("rental_services")} />
                </div>
                <div>
                  <label htmlFor="s-brands" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.brands)}</label>
                  <input id="s-brands" data-testid="supplier-brands-input" className={inputCls("brands")} value={form.brands} onChange={set("brands")} />
                </div>
                <div>
                  <label htmlFor="s-website" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.website)}</label>
                  <input id="s-website" type="url" data-testid="supplier-website-input" placeholder="https://" className={inputCls("website")} value={form.website} onChange={set("website")} />
                </div>
                <div>
                  <label htmlFor="s-file" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.file)}</label>
                  <input id="s-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="supplier-file-input" onChange={onFile}
                    className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-graphite file:mr-3 file:rounded-full file:border-0 file:bg-badge file:px-4 file:py-2 file:text-xs file:font-bold file:text-sky-700" />
                  {fileError && <p role="alert" className="mt-1 text-xs text-red-500">{fileError}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="s-comments" className="mb-1.5 block text-[13px] font-bold text-navy-900">{t(SUPPLIERS.comments)}</label>
                <textarea id="s-comments" rows={3} data-testid="supplier-comments-textarea" className={inputCls("comments")} value={form.comments} onChange={set("comments")} />
              </div>

              {/* Honeypot antispam */}
              <div className="absolute left-[-9999px]" aria-hidden="true">
                <label htmlFor="s-website-hp">Ne pas remplir</label>
                <input id="s-website-hp" type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set("company_website")} />
              </div>

              <p className="mt-6 flex items-start gap-3 rounded-xl border border-sky-200 bg-ice p-4 text-[12.5px] leading-relaxed text-navy-900">
                <Info size={16} className="mt-0.5 shrink-0 text-electric" />
                {t(SUPPLIERS.verification)}
              </p>

              <div className="mt-5">
                <label className="flex cursor-pointer items-start gap-3" data-testid="supplier-consent-checkbox">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 rounded border-slate-300 accent-electric" aria-invalid={!!errors.consent} />
                  <span className="text-[13px] leading-relaxed text-graphite">{t(SUPPLIERS.consent)}</span>
                </label>
                {err("consent")}
              </div>

              {status === "error" && (
                <div role="alert" className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="supplier-form-error">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{t(SUPPLIERS.error)} {serverError && `(${serverError})`}</span>
                </div>
              )}

              <button type="submit" disabled={status === "sending"} data-testid="supplier-form-submit"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-electric-dark disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {status === "sending" ? t(SUPPLIERS.sending) : t(SUPPLIERS.submit)}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
